import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, flow, makeObservable, observable, reaction } from "mobx";
import { CategoryNode } from "../models/categoryNode.js";
import { __ } from "../utils/i18n.js";
import { request } from "../utils/request.js";
import { locationRestHierarchyPut } from "../wp-api/hierarchy.put.js";
import { locationRestTermsPost } from "../wp-api/terms.post.js";
import { locationRestTreeGet } from "../wp-api/tree.get.js";
class TreeStore {
    static ID_ALL = "ALL";
    staticTree = [];
    tree = [];
    selected;
    selectedId;
    busy = false;
    refs = new Map();
    selectedChangeListeners = new Set();
    rootStore;
    constructor(rootStore){
        this.rootStore = rootStore;
        makeObservable(this);
        // Listen to taxonomy changes and refetch tree
        reaction(()=>this.rootStore.optionStore.others.taxnow, ()=>this.fetchTree({
                remember: true
            }));
        // Make the init async to avoid stack overflow
        setTimeout(this.init.bind(this));
    }
    subscribeSelectedChange(listener) {
        this.selectedChangeListeners.add(listener);
        return ()=>this.selectedChangeListeners.delete(listener);
    }
    /**
     * Single source of truth for tree selection (`selectedId`). Node `selected` flags are a UI
     * projection maintained here so react-folder-tree can spread nodes into TreeNode props.
     */ setSelected(node) {
        if (this.selected && this.selected !== node) {
            this.selected.selected = false;
        }
        this.selected = node;
        this.selectedId = node?.id;
        if (node) {
            node.selected = true;
        }
        this.selectedChangeListeners.forEach((listener)=>listener());
    }
    init() {
        const allNode = new CategoryNode({
            id: "ALL",
            title: __("All posts"),
            icon: "copy",
            count: this.rootStore.optionStore.others.allPostCnt
        }, this);
        this.staticTree.push(allNode);
        this.setSelected(allNode);
        // Initially fetch tree
        if (this.rootStore.optionStore.others.screenSettings.isActive) {
            this.fetchTree();
        }
    }
    byId(id, excludeStatic = true) {
        let element = this.refs.get(id);
        if (!element && typeof id === "number") {
            element = this.refs.get(String(id));
        }
        if (!element && typeof id === "string" && /^-?\d+$/.test(id)) {
            element = this.refs.get(+id);
        }
        if (excludeStatic && element && this.staticTree.indexOf(element) > -1) {
            return undefined;
        }
        return element;
    }
    fetchTree = flow(function*(params, callback) {
        this.busy = true;
        try {
            const { taxnow, typenow } = this.rootStore.optionStore.others;
            if (!taxnow || !typenow) {
                return;
            }
            const result = yield request({
                location: locationRestTreeGet,
                sendReferer: true,
                params: Object.assign({
                    remember: false,
                    taxonomy: taxnow,
                    type: typenow
                }, params)
            });
            const { selectedId: apiSelectedId, tree } = result;
            this.tree = tree.map(CategoryNode.mapFromRestEndpoint.bind(this));
            const flatTree = (nodes)=>nodes.flatMap((node)=>[
                        node,
                        ...flatTree(node.childNodes)
                    ]);
            const resolveSelectionByUrl = ()=>{
                if (typeof window === "undefined") {
                    return undefined;
                }
                const urlParams = new URL(window.location.href).searchParams;
                const categoryNameFromUrl = urlParams.get("category_name");
                if (categoryNameFromUrl) {
                    const categoryNode = flatTree(this.tree).find((node)=>`${node.properties?.category_name ?? ""}` === categoryNameFromUrl);
                    if (categoryNode) {
                        return categoryNode.id;
                    }
                }
                const matchingNode = flatTree(this.tree).find((node)=>{
                    const queryArgs = node.properties.queryArgs ?? {};
                    const entries = Object.entries(queryArgs);
                    if (!entries.length) {
                        return false;
                    }
                    return entries.every(([key, value])=>urlParams.get(key) === `${value}`);
                });
                return matchingNode?.id;
            };
            const selectedByUrl = resolveSelectionByUrl();
            const selectedNodeId = selectedByUrl ?? this.selectedId ?? apiSelectedId ?? TreeStore.ID_ALL;
            const selectedNode = this.byId(selectedNodeId, false);
            if (selectedNode) {
                this.setSelected(selectedNode);
            }
            callback?.(result);
        } finally{
            this.busy = false;
        }
    });
    persist = flow(function*(data) {
        const result = yield request({
            location: locationRestTermsPost,
            request: data
        });
        const newObj = CategoryNode.mapFromRestEndpoint.apply(this, [
            {
                category_name: result.category_name,
                childNodes: [],
                count: result.count,
                editableSlug: result.editableSlug,
                name: result.name,
                post_type: result.post_type,
                queryArgs: result.queryArgs,
                taxonomy: result.taxonomy,
                term_id: result.term_id
            }
        ]);
        const parentId = +data.parent;
        if (!parentId) {
            this.tree.push(newObj);
        } else {
            this.byId(parentId)?.addChildNode(newObj);
        }
        return newObj;
    });
    sort = flow(function*({ id, oldIndex, newIndex, parentFromId, parentToId, nextId, ...rest }) {
        // Find node which gets moved and detach it from tree
        const fromList = parentFromId === 0 ? this.tree : this.byId(parentFromId).childNodes;
        const toList = parentToId === 0 ? this.tree : this.byId(parentToId).childNodes;
        const movement = fromList[oldIndex];
        fromList.splice(oldIndex, 1);
        toList.splice(newIndex, 0, movement);
        if (!rest.request) {
            return true;
        }
        const { typenow, taxnow } = this.rootStore.optionStore.others;
        // Request to backend
        try {
            yield request({
                location: locationRestHierarchyPut,
                params: {
                    id
                },
                request: {
                    nextId,
                    parent: parentToId,
                    type: typenow,
                    taxonomy: taxnow
                }
            });
            return true;
        } catch (e) {
            yield this.sort({
                id,
                oldIndex: newIndex,
                newIndex: oldIndex,
                parentFromId: parentToId,
                parentToId: parentFromId,
                nextId,
                request: false
            });
            throw e;
        }
    });
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], TreeStore.prototype, "staticTree", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], TreeStore.prototype, "tree", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof CategoryNode === "undefined" ? Object : CategoryNode)
], TreeStore.prototype, "selected", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], TreeStore.prototype, "selectedId", void 0);
_ts_decorate([
    observable
], TreeStore.prototype, "busy", void 0);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof CategoryNode === "undefined" ? Object : CategoryNode
    ]),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "setSelected", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "init", null);
export { TreeStore };
